import React, { useMemo } from "react";
import { Box, Group, SimpleGrid, Stack, Text, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  DATA_AR_COMMISSION_ACCOUNT_TYPE,
  DATA_AR_SUBMISSION_VIA,
} from "../data/options";
import { getArFormValues } from "../helpers/arFormUtils";
import {
  buildCommissionChargeSummary,
  formatCommissionPercent,
  formatGroupedAmount,
} from "../helpers/investmentTransactionCommissionUtils";
import { useInvestmentTransactionSettings } from "../contexts/investmentTransactionSettingsContext";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";

const resolveLabel = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value ?? "—";

const Fact = ({ label, value, hint }) => (
  <Box>
    <Text size="xs" color="dimmed">
      {label}
    </Text>
    <Text size="sm" weight={600} mt={2}>
      {value}
    </Text>
    {hint ? (
      <Text size="xs" color="dimmed" mt={2}>
        {hint}
      </Text>
    ) : null}
  </Box>
);

const Step = ({ index, title, detail, equation, result }) => {
  const theme = useMantineTheme();

  return (
    <Group align="flex-start" noWrap spacing="sm">
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 11,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: theme.colors.cyan[0],
          color: theme.colors.cyan[9],
        }}
      >
        <Text size="xs" weight={700}>
          {index}
        </Text>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Text size="sm" weight={600}>
          {title}
        </Text>
        <Text size="xs" color="dimmed" mt={2}>
          {detail}
        </Text>
        <Text size="sm" mt={6}>
          {equation}
          <Text component="span" weight={700}>
            {result}
          </Text>
        </Text>
      </Box>
    </Group>
  );
};

const OrderInvestmentTransactionCommission = ({ form }) => {
  const { t } = useTranslation();
  const arForm = getArFormValues(form);
  const commission = arForm.commission ?? {};
  const submissionVia = arForm.submissionVia || "Online";
  const {
    nonWrapMaxSubscriptionFee,
    onlineIfastCharge,
    paperIfastCharge,
  } = useInvestmentTransactionSettings();

  const chargeSummary = useMemo(
    () =>
      buildCommissionChargeSummary({
        submissionVia,
        commission,
        investmentAmount: arForm.investmentAmount,
        rates: {
          nonWrapMaxSubscriptionFee,
          onlineIfastCharge,
          paperIfastCharge,
        },
      }),
    [
      submissionVia,
      commission,
      arForm.investmentAmount,
      nonWrapMaxSubscriptionFee,
      onlineIfastCharge,
      paperIfastCharge,
    ]
  );

  if (chargeSummary?.commissionAmount == null) {
    return (
      <OrderArFormStack>
        <OrderArFormBlock
          title={t("iFast Charge Breakdown")}
          subtitle="iFast Master Logsheet — charge split from client upfront fee rate"
        >
          <Text size="sm" color="dimmed">
            Complete Part A (investment amount) and Part C (account type and fee
            rate) on the Transaction Detail tab to see the commission breakdown.
          </Text>
        </OrderArFormBlock>
      </OrderArFormStack>
    );
  }

  const isNonWrap = chargeSummary.accountType === "NonWrap";
  const isPaper = chargeSummary.submissionVia === "Paper";
  const channelName = isPaper ? "paper" : "online";
  const fee = formatCommissionPercent(chargeSummary.upfrontFee);
  const channelRate = formatCommissionPercent(
    chargeSummary.channelIfastChargePercent
  );
  const ifast = formatCommissionPercent(chargeSummary.ifastCharge);
  const rate = formatCommissionPercent(chargeSummary.commissionRate);
  const investment = formatGroupedAmount(chargeSummary.investmentAmount);
  const commissionAmount = formatGroupedAmount(chargeSummary.commissionAmount);
  const maxSubscription = formatCommissionPercent(
    chargeSummary.nonWrapMaxSubscriptionFee
  );
  const ifastBase = isNonWrap ? maxSubscription : fee;

  return (
    <OrderArFormStack>
      <OrderArFormBlock
        title="Commission"
        subtitle={`${resolveLabel(
          DATA_AR_COMMISSION_ACCOUNT_TYPE,
          chargeSummary.accountType
        )} · ${resolveLabel(DATA_AR_SUBMISSION_VIA, chargeSummary.submissionVia)}`}
      >
        <Stack spacing="lg">
          <Box>
            <Text size="xl" weight={700}>
              HK$ {commissionAmount}
            </Text>
            <Text size="xs" color="dimmed" mt={4}>
              Investment amount × commission rate
            </Text>
          </Box>

          <SimpleGrid
            cols={3}
            spacing="md"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            <Fact label="Investment amount" value={`HK$ ${investment}`} />
            <Fact label="Upfront fee / switch fee" value={fee} />
            <Fact
              label="Commission rate"
              value={rate}
              hint={`${fee} − ${ifast}`}
            />
          </SimpleGrid>

          <Stack spacing="md">
            <Text size="sm" weight={600}>
              {t("iFast Charge Breakdown")}
            </Text>

            <Step
              index="1"
              title="iFast charge"
              detail={
                isNonWrap
                  ? `Non-Wrap uses the max subscription fee × the ${channelName} iFast charge. It does not use the client fee.`
                  : `Wrap multiplies the upfront fee by the ${channelName} iFast charge. ${channelRate} applies to the fee, not to the investment amount.`
              }
              equation={`${ifastBase} × ${channelRate} = `}
              result={ifast}
            />
            <Step
              index="2"
              title="Commission rate"
              detail="Upfront fee / switch fee minus the iFast charge."
              equation={`${fee} − ${ifast} = `}
              result={rate}
            />
            <Step
              index="3"
              title="Commission"
              detail="Commission rate applied to the investment amount."
              equation={`HK$ ${investment} × ${rate} = `}
              result={`HK$ ${commissionAmount}`}
            />
          </Stack>
        </Stack>
      </OrderArFormBlock>
    </OrderArFormStack>
  );
};

export default OrderInvestmentTransactionCommission;
